import { Directive, Input, Output, EventEmitter, ElementRef, OnInit, OnDestroy, inject } from '@angular/core';
import { defer, fromEvent, merge, Subject } from 'rxjs';
import { filter, first, switchMap, takeUntil, tap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

export interface IrisDragAndDropEventI {
  dragData: DataTransfer;
  dropData: Record<string, string>;
}

const dragId = uuid();

@Directive({
  selector: '[irisDragAndDrop]',
})
export class IrisDragAndDropDirective implements OnInit, OnDestroy {
  hostElement = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input() dragData: Record<string, string> = {};
  @Input() dropData: Record<string, string> = {};
  @Input() draggingPicture: HTMLElement;

  private _draggable = true;
  @Input() set draggable(draggable: boolean) {
    const valueChanged = this.draggable !== draggable;
    this._draggable = draggable;
    if (valueChanged) {
      this.setDraggableAttributes();
    }
  }
  get draggable(): boolean {
    return this._draggable;
  }

  private _droppable = true;
  @Input() set droppable(droppable: boolean) {
    const valueChanged = this.droppable !== droppable;
    this._droppable = droppable;
    if (valueChanged) {
      this.initDropEvents();
    }
  }
  get droppable(): boolean {
    return this._droppable;
  }

  @Input() set handlerElements(handlerElements: HTMLElement[]) {
    this.stopDragEvents();
    this.stopDropEvents();
    this.resetDragAttributes();
    this.resetChildrenPointerEvents();
    this._handlerElements = handlerElements?.length ? handlerElements : [this.nativeElement];
    this.setDraggableAttributes();
    this.initDragEvents();
    this.initDropEvents();
  }

  get handlerElements(): HTMLElement[] {
    return this._handlerElements;
  }

  private _handlerElements: HTMLElement[] = [this.nativeElement];
  private readonly DRAGOVER_CLEANUP_DELAY = 5000;
  private dragOverTimer = null;

  @Output() dropped = new EventEmitter<IrisDragAndDropEventI>();
  @Output() dragStart = new EventEmitter<IrisDragAndDropEventI>();
  @Output() dragEnd = new EventEmitter<IrisDragAndDropEventI>();
  @Output() dragginOver = new EventEmitter<boolean>();

  private readonly unsubscribeDragEvents$ = new Subject<void>();
  private readonly unsubscribeDropEvents$ = new Subject<void>();

  public get nativeElement(): HTMLElement {
    return this.hostElement.nativeElement;
  }

  private get extendedDropData(): Record<string, string> {
    return { ...this.dropData, dragId };
  }

  ngOnInit(): void {
    if (this.draggable) {
      this.setDraggableAttributes();
    }
    this.initDragEvents();
    this.initDropEvents();
  }

  initDragEvents(): void {
    this.stopDragEvents();
    this.handlerElements.forEach(element => this.toggleDragEvents(element));
  }

  toggleDragEvents(handlerElement: HTMLElement): void {
    if (!this.draggable) { return; }

    const dragStart$ = fromEvent(handlerElement, 'dragstart');
    const dragEnd$ = fromEvent(handlerElement, 'dragend');

    dragStart$.pipe(
      filter(() => this.draggable),
      tap((event: DragEvent) => this.dragStarted(event)),
      takeUntil(this.unsubscribeDragEvents$),
    ).subscribe();

    dragEnd$.pipe(
      filter(() => this.draggable),
      tap((event: DragEvent) => this.dragEnded(event)),
      takeUntil(this.unsubscribeDragEvents$),
    ).subscribe();
  }

  initDropEvents(): void {
    this.resetChildrenPointerEvents();
    this.stopDropEvents();
    this.handlerElements.forEach(element => this.toggleDropEvents(element));
  }

  toggleDropEvents(handlerElement: HTMLElement): void {
    handlerElement.classList.toggle('droppable', this.droppable);
    if (!this.droppable) {
      return;
    }
    const dragEnter$ = defer(() => fromEvent(handlerElement, 'dragenter'))
      .pipe(
        filter(() => this.droppable),
        tap(() => this.setChildrenPointerEvents(handlerElement, true)),
        tap((event: DragEvent) => this.dragEnter(event)),
        takeUntil(this.unsubscribeDropEvents$),
      );

    const dragLeave$ = defer(() => fromEvent(handlerElement, 'dragleave'))
      .pipe(
        tap(() => this.setChildrenPointerEvents(handlerElement, false)),
        tap((event: DragEvent) => this.dragLeave(event)),
        takeUntil(this.unsubscribeDropEvents$),
      );

    const dragOver$ = defer(() => fromEvent(handlerElement, 'dragover'))
      .pipe(
        tap((event: DragEvent) => this.dragOver(event)),
        takeUntil(this.unsubscribeDropEvents$),
      );

    const dragEnd$ = defer(() => fromEvent(handlerElement, 'dragend'))
      .pipe(
        tap(() => this.setChildrenPointerEvents(handlerElement, false)),
        tap((event: DragEvent) => this.dragEnded(event)),
        takeUntil(this.unsubscribeDropEvents$),
      );

    const drop$ = defer(() => fromEvent(handlerElement, 'drop'))
      .pipe(
        tap(() => this.setChildrenPointerEvents(handlerElement, false)),
        tap((event: DragEvent) => this.onDropped(event)),
        takeUntil(this.unsubscribeDropEvents$),
      );

    dragEnter$.pipe(
      switchMap(() => dragOver$),
      switchMap(() => merge(dragLeave$.pipe(first()),  drop$.pipe(takeUntil(dragEnd$)))),
      takeUntil(this.unsubscribeDropEvents$),
    ).subscribe();
  }

  dragStarted(event: DragEvent): void {
    for (const itemName in this.dragData) {
      // eslint-disable-next-line no-prototype-builtins
      if (this.dragData.hasOwnProperty(itemName)) {
        this.getDataTransfer(event)?.setData(itemName, this.dragData[itemName]);
      }
    }

    this.getDataTransfer(event)?.setData('dragId', dragId);

    // implement dragType variable if drag not lines (to point cursor correctly)
    const handlerElement = this.draggingPicture ?? (event.target as HTMLElement);
    this.getDataTransfer(event)?.setDragImage(handlerElement, 0, 0);

    this.dragStart.emit({ dragData: this.getDataTransfer(event), dropData: this.extendedDropData });
  }

  dragEnter(event: DragEvent): void {
    event.preventDefault();
    this.toggleDragoverClass(true);
    this.getDataTransfer(event).dropEffect = 'copy';
  }

  dragOver(event: DragEvent): void {
    event.preventDefault();
    this.toggleDragoverClass(true);
    this.dragginOver.emit(true);
  }

  dragLeave(event: DragEvent): void {
    event.preventDefault();
    this.toggleDragoverClass(false);
    this.dragginOver.emit(false);
  }

  dragEnded(event: DragEvent): void {
    event.preventDefault();
    this.toggleDragoverClass(false);
    this.dragginOver.emit(false);
    this.dragEnd.emit({ dragData: this.getDataTransfer(event), dropData: this.extendedDropData });
  }

  onDropped(event: DragEvent): void {
    event.preventDefault();

    this.dragginOver.emit(false);
    this.dropped.emit({ dragData: this.getDataTransfer(event), dropData: this.extendedDropData });
    this.toggleDragoverClass(false);
  }

  setChildrenPointerEvents(handlerElement: HTMLElement, enable: boolean): void {
    handlerElement.childNodes.forEach((child: HTMLElement) => {
      if (!child.style) {
        return;
      }
      if (enable) {
        child.style.pointerEvents = 'none';
      } else {
        child.style.removeProperty('pointer-events');
      }
    });

    if (enable) {
      if (this.dragOverTimer) {
        clearTimeout(this.dragOverTimer);
      }
      this.dragOverTimer = setTimeout(() => this.setChildrenPointerEvents(handlerElement, !enable), this.DRAGOVER_CLEANUP_DELAY);
    }
  }

  resetChildrenPointerEvents(): void {
    this.handlerElements.forEach(element => this.setChildrenPointerEvents(element, false));
  }

  getDataTransfer(event: DragEvent): DataTransfer {
    return event.dataTransfer;
  }

  setDraggableAttributes(): void {
    this.handlerElements.forEach(element => this.setDraggableAttribute(element));
  }

  setDraggableAttribute(handlerElement: HTMLElement): void {
    handlerElement.setAttribute('draggable', this.draggable.toString());
  }

  resetDragAttributes(): void {
    this.handlerElements.forEach(element => element.setAttribute('draggable', 'false'));
  }

  stopDragEvents(): void {
    this.unsubscribeDragEvents$.next();
  }

  stopDropEvents(): void {
    this.unsubscribeDropEvents$.next();
  }

  toggleDragoverClass(force: boolean): void {
    this.handlerElements.forEach(element => element.classList.toggle('iris-drag-over', force));
    if (force) {
      if (this.dragOverTimer) {
        clearTimeout(this.dragOverTimer);
      }
      this.dragOverTimer = setTimeout(() => this.toggleDragoverClass(!force), this.DRAGOVER_CLEANUP_DELAY);
    }
  }

  ngOnDestroy(): void {
    this.stopDragEvents();
    this.stopDropEvents();
    this.unsubscribeDropEvents$.complete();
    this.unsubscribeDragEvents$.complete();
  }
}
